import {Button, ButtonGroup, closeCurrentDialog} from 'platform/components';
import {Space, Text, VStack} from 'platform/foundation';

import {
  PutServiceOrderItemsDiscountApiArg,
  usePutServiceOrderItemsDiscountMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

type ServiceOrderDiscountBody = Omit<
  NonNullable<PutServiceOrderItemsDiscountApiArg['body']>,
  'isDiscountApply'
>;

interface DiscountConfirmationDialogContentProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  serviceOrderDiscount: ServiceOrderDiscountBody;
}

export function DiscountConfirmationDialogContent(props: DiscountConfirmationDialogContentProps) {
  const [putServiceOrderItemsDiscount, {isLoading: isPutServiceOrderItemsDiscountLoading}] =
    usePutServiceOrderItemsDiscountMutation();

  const submitDiscountWithApplyFlag = async (isDiscountApply: boolean) => {
    await putServiceOrderItemsDiscount({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
      body: {
        ...props.serviceOrderDiscount,
        isDiscountApply,
      },
    })
      .unwrap()
      .catch(handleApiError);

    closeCurrentDialog();
  };

  const handleDiscard = () => {
    closeCurrentDialog();
  };

  const handleDoNotApply = () => {
    submitDiscountWithApplyFlag(false);
  };

  const handleApply = () => {
    submitDiscountWithApplyFlag(true);
  };

  return (
    <>
      <VStack>
        <Text alternative>{i18n.t('entity.order.discountConfirmation.question')}</Text>
        <Space vertical={4} />
        <Text size="small" color="tertiary">
          • {i18n.t('entity.order.discountConfirmation.doNotApplyDescription')}
        </Text>
        <Text size="small" color="tertiary">
          • {i18n.t('entity.order.discountConfirmation.applyDescription')}
        </Text>
      </VStack>
      <Space vertical={4} />
      <ButtonGroup align="right">
        <Button
          variant="secondary"
          title={i18n.t('general.actions.discard')}
          isLoading={isPutServiceOrderItemsDiscountLoading}
          onClick={handleDiscard}
          data-testid={suffixTestId('discard', props)}
        />
        <Button
          variant="secondary"
          title={i18n.t('entity.order.discountConfirmation.doNotApply')}
          isLoading={isPutServiceOrderItemsDiscountLoading}
          onClick={handleDoNotApply}
          data-testid={suffixTestId('doNotApply', props)}
        />
        <Button
          variant="primary"
          title={i18n.t('general.actions.apply')}
          isLoading={isPutServiceOrderItemsDiscountLoading}
          onClick={handleApply}
          data-testid={suffixTestId('apply', props)}
        />
      </ButtonGroup>
    </>
  );
}
